import { useState, useRef, useEffect } from 'react';
import { useActiveChild } from '../context/ActiveChildContext';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, UserPlus, Users, Check } from 'lucide-react';

/**
 * Sidebar pill that lets a parent see and switch between their linked children.
 * - Hidden when not a parent.
 * - Shows "Add Child" CTA when parent has 0 children linked.
 * - Shows the active child name + dropdown when 1+ children exist.
 * Selecting a child updates ActiveChildContext, which child-aware pages read
 * to scope their data (Decision Lab, Pathway, Report, etc.).
 */
export default function ActiveChildPill({ collapsed = false }) {
  const { isParent, children, activeChild, setActiveChildId } = useActiveChild();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!isParent) return null;
  if (collapsed) return null; // hide when sidebar is icon-only

  // No children linked yet → CTA
  if (!children || children.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate('/app/family')}
        data-testid="active-child-add-cta"
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 hover:border-purple-400/60 transition-colors"
      >
        <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
          <UserPlus className="w-4 h-4 text-purple-300" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-medium text-white">Add a child</p>
          <p className="text-xs text-purple-300/70">Link your child's journey</p>
        </div>
      </button>
    );
  }

  const initials = (activeChild?.full_name || activeChild?.name || activeChild?.first_name || 'C')
    .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="active-child-pill"
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:border-blue-500/60 transition-colors"
      >
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shrink-0 text-white text-xs font-semibold">
          {initials}
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-300">Viewing</p>
          <p className="text-sm font-medium text-white truncate" data-testid="active-child-name">
            {activeChild?.full_name || activeChild?.name || activeChild?.first_name || 'Pick a child'}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-300 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-50 rounded-xl border border-slate-700/80 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden"
          data-testid="active-child-dropdown"
        >
          <div className="px-3 py-2 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-slate-300" />
              <p className="text-[10px] uppercase tracking-wider text-slate-300">Your children</p>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {children.map((c) => {
              const id = c.id || c.user_id;
              const name = c.full_name || c.name || c.first_name || c.email || 'Child';
              const isActive = activeChild && (activeChild.id || activeChild.user_id) === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setActiveChildId(id); setOpen(false); }}
                  data-testid={`active-child-option-${id}`}
                  className={`w-full px-3 py-2 flex items-center gap-3 text-left transition-colors ${
                    isActive ? 'bg-blue-500/15' : 'hover:bg-slate-800/80'
                  }`}
                >
                  <div className="w-7 h-7 rounded-md bg-slate-700/70 flex items-center justify-center text-[10px] font-semibold text-white shrink-0">
                    {(name.split(' ').map(s => s[0]).slice(0, 2).join('') || '?').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{name}</p>
                    {c.grade && <p className="text-[11px] text-slate-300">Grade {c.grade}</p>}
                  </div>
                  {isActive && <Check className="w-4 h-4 text-blue-400 shrink-0" />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/app/family'); }}
            data-testid="active-child-manage"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left text-xs text-slate-300 hover:text-white hover:bg-slate-800/80 border-t border-slate-800 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Manage children
          </button>
        </div>
      )}
    </div>
  );
}
